<template>
  <ConfirmDialog
      v-model="dialog"
      @update:agree="agree"
      @update:cancel="cancel"
      ref="confirm"
  >
    <template v-slot:title>
      {{ $t(title) }}
    </template>
    <template v-slot:content>
      <v-card-text class="pa-4 text-black">
        <div class="d-flex flex-column ga-5">
          <AutoCompleteInput
            label="Поиск по ИНН и наименованию поставщика"
            v-model="selectedItem"
            v-model:search="searchInput"
            no-data-text="Отсутствуют данные"
            no-filter
            :loading="contragent.loading"
            :items="contragent.items"
            :disabled="processing"
            @update:search="handleSearchContragent"
            @update:modelValue="handleContragentSelect"
            item-title="fullName"
            item-value="id"
          />
          <div class="d-flex flex-column ga-3 suppliers-wrapper">
            <span class="title">{{ $t('Приглашенные участники') }}</span>
            <div
                v-for="(contragent, index) in allowedContragents"
                :key="contragent.id"
                class="d-flex flex-row align-center justify-space-between"
                :class="{ 'border-bottom': index !== allowedContragents.length - 1 }"
            >
              <div class="d-flex flex-column ga-1">
                <span>{{ contragent.fullName }}</span>
                <span class="subtitle">{{ `${contragent.inn} / ${contragent.kpp}` }}</span>
                <span class="subtitle">{{ contragent.email }}</span>
              </div>
              <div class="d-flex flex-row ga-1 align-center">
                <v-btn
                    variant="text"
                    icon="custom:eyeOutlineIcon"
                    @click="viewContragent(contragent.id)"
                ></v-btn>
                <v-btn
                    v-if="!hasActiveApplication(contragent.id)"
                    variant="text"
                    icon="custom:deleteOutlineIcon"
                    @click="removeContragent(contragent.id)"
                ></v-btn>
              </div>
            </div>
          </div>
          <SelectInput
              label="Тип доступа"
              v-model="isClosed"
              :items="types"
              :disabled="applications.length > 0"
              item-title="label"
              item-value="value"
          />
        </div>
      </v-card-text>
    </template>
  </ConfirmDialog>
</template>

<script>
import api from "@/api";
import AutoCompleteInput from "@intec/vue-components/form/AutoCompleteInput.vue";
import SelectInput from "@intec/vue-components/form/SelectInput.vue";
import ConfirmDialog from "@intec/vue-components/form/ConfirmDialog.vue";
import { PROCEDURE_ACCESS_TYPE } from "@/lib/procedure";

export default {
  name: 'ProcedureAccessDlg',
  components: {
    ConfirmDialog,
    AutoCompleteInput,
    SelectInput,
  },
  data() {
    return {
      dialog: false,
      resolve: null,
      reject: null,
      title: null,
      valid: true,
      options: {
        color: 'grey lighten-3',
        width: 625,
        zIndex: 200
      },
      selectedItem: null,
      searchInput: null,
      processing: false,
      contragent: {
        loading: false,
        items: [],
      },
      isClosed: false,
      types: [
        { label: PROCEDURE_ACCESS_TYPE.TYPE_OPEN, value: false },
        { label: PROCEDURE_ACCESS_TYPE.TYPE_CLOSED, value: true }
      ],
      allowedContragents: [],
      applications: [],
    };
  },
  methods: {
    open(
      title,
      isClosed = false,
      allowedContragents = [],
      applications = [],
      options = {}
    ) {
      this.dialog = true;
      this.title = title;
      this.isClosed = isClosed;
      this.allowedContragents = [...allowedContragents];
      this.applications = applications || [];
      this.options = Object.assign({}, this.options, options);

      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },
    agree() {
      this.dialog = false;
      this.resolve({
        confirmed: true,
        data: {
          isClosed: this.isClosed,
          allowedContragents: this.allowedContragents,
        },
      });
    },
    cancel() {
      this.dialog = false;
      this.resolve({
        confirmed: false,
        data: null
      });
    },
    handleContragentSelect(selectedId) {
      const selected = this.contragent.items.find(item => item.id === selectedId);
      if (!selected) return;

      // Проверка на дубликаты
      const exists = this.allowedContragents.some(c => c.id === selected.id);
      if (exists) return;

      this.allowedContragents.push(selected);

      this.$nextTick(() => {
        this.selectedItem = null;
        this.searchInput = null;
      });
    },
    handleSearchContragent(val) {
      if (val) {
        this.queryContragentSelections(val);
        return;
      }

      this.contragent.items = [];
      this.selectedItem = null;
      this.searchInput = null;
    },
    async queryContragentSelections(v) {
      if (this.contragent.loading) return;

      let query = {
        search: v,
        filter: null,
        range: {},
        sort: [{ column: 'c.fullName', order: 'ASC' }],
      };

      this.contragent.loading = true;

      const contragentIds = this.allowedContragents.map(c => c.id);

      const response = await api.get('/procedures/contragents', {
        query: JSON.stringify(query),
        excludeCurrent: true,
        onlySuppliers: true,
      });

      this.contragent.items = response.entries
        .map(item => {
          const c = item.c;
          return {
            id: c.id,
            fullName: c.fullName,
            inn: c.inn,
            kpp: c.kpp,
            email: c.email,
          };
        })
        .filter(item => !contragentIds.includes(item.id));
      this.contragent.loading = false;

      if (!this.searchInput) {
        this.contragent.items = [];
        this.selectedItem = null;
      }
    },
    viewContragent(contragentId) {
      let route = this.$router.resolve({
        name: 'profile.contragent',
        params: { id: contragentId },
      });
      window.open(route.href, '_blank');
    },
    removeContragent(contragentId) {
      this.allowedContragents = this.allowedContragents.filter(contragent => contragent.id !== contragentId);
    },
    hasActiveApplication(contragentId) {
      return this.applications.some(app => app.contragentId === contragentId);
    },
  },
};
</script>
<style scoped>
.suppliers-wrapper {
  font-size: 14px;
  font-weight: 500;

  .title {
    font-size: 16px;
    font-weight: 600;
  }

  .border-bottom {
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  }

  .subtitle {
    color: rgb(var(--unactive-text-color));
  }
}
</style>
